import { useTranslation } from "react-i18next"
import type { NotificationDto } from "@/entities/notification/types"
import { NotificationSeverity, SEVERITY_ICON } from "@/entities/notification/types"
import { formatNotificationDisplayFields } from "@/features/notifications/formatDepositNotificationCopy"
import { formatNotificationTime } from "@/widgets/notifications/formatNotificationTime"
import { resolveIntlLocale } from "@/shared/lib/intl/resolveIntlLocale"

const severityTone: Record<NotificationSeverity, string> = {
  0: "text-gray-500",
  1: "text-emerald-600",
  2: "text-amber-600",
  3: "text-brand-500",
}

type Props = {
  notification: NotificationDto
  onActivate: (n: NotificationDto) => void
  compact?: boolean
}

export const NotificationItem = ({ notification, onActivate, compact = false }: Props) => {
  const { t, i18n } = useTranslation()
  const { title: displayTitle, body: displayBody } = formatNotificationDisplayFields(notification, t)
  const time = formatNotificationTime(notification.createdAt, resolveIntlLocale(i18n.language || "ru"))
  const toneClass = severityTone[notification.severity] ?? "text-gray-500"

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onActivate(notification)
    }
  }

  const unread = !notification.isRead

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onActivate(notification)}
      onKeyDown={handleKeyDown}
      className={`group relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-0 ${
        unread ? "bg-[#F5E6D3]/30 hover:bg-[#F5E6D3]/60" : "bg-white hover:bg-gray-50"
      }`}
      aria-label={
        unread ? t("notifications.item.markReadAria", { title: displayTitle }) : displayTitle
      }
    >
      {unread ? (
        <span aria-hidden className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-brand-500" />
      ) : null}
      <div className={`shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center ${toneClass}`} aria-hidden>
        <i className={`${SEVERITY_ICON[notification.severity] ?? "ri-information-line"} text-lg`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={`text-sm leading-snug ${unread ? "font-semibold text-gray-900" : "font-medium text-gray-700"} break-words`}
          >
            {displayTitle}
          </p>
          <span className="shrink-0 text-[11px] text-gray-400 whitespace-nowrap">{time}</span>
        </div>
        {!compact && displayBody ? (
          <p className="text-xs text-gray-600 mt-0.5 leading-relaxed break-words line-clamp-2">{displayBody}</p>
        ) : null}
      </div>
    </div>
  )
}
